import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const EMPTY = {
  ruc: "", business_name: "", trade_name: "", contact_name: "",
  phone: "", email: "", address: "", city: "", notes: "", active: true,
};

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/suppliers/${editing}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Proveedor actualizado" : "Proveedor creado");
      setForm(EMPTY); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditing(s.id); setShowForm(true); };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar proveedor?")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="suppliers-page">
      <PageHeader
        title="Proveedores"
        subtitle="Gestión de proveedores de flores, insumos y accesorios."
        testid="suppliers-header"
        action={
          <button
            data-testid="suppliers-create-button"
            onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuevo proveedor
          </button>
        }
      />

      {showForm && (
        <div data-testid="supplier-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} proveedor</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="RUC *"><input data-testid="supplier-ruc" required className="input-soft" value={form.ruc} onChange={(e) => setForm({ ...form, ruc: e.target.value })} /></Field>
            <Field label="Razón social *"><input data-testid="supplier-business-name" required className="input-soft" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></Field>
            <Field label="Nombre comercial"><input className="input-soft" value={form.trade_name || ""} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} /></Field>
            <Field label="Contacto"><input className="input-soft" value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
            <Field label="Teléfono"><input className="input-soft" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><input type="email" className="input-soft" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Dirección"><input className="input-soft" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Ciudad"><input className="input-soft" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Notas"><textarea className="input-soft" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="supplier-save-button" className="btn-primary" type="submit">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin proveedores aún" hint="Agrega tu primer proveedor para empezar." testid="suppliers-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="suppliers-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">RUC</th><th className="px-5 py-3">Razón social</th>
                <th className="px-5 py-3">Contacto</th><th className="px-5 py-3">Teléfono</th>
                <th className="px-5 py-3">Email</th><th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-4 font-mono">{s.ruc}</td>
                  <td className="px-5 py-4 font-medium">{s.business_name}</td>
                  <td className="px-5 py-4">{s.contact_name || "—"}</td>
                  <td className="px-5 py-4">{s.phone || "—"}</td>
                  <td className="px-5 py-4">{s.email || "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => onEdit(s)} className="p-2 rounded hover:bg-[#F3F2ED]"><Pencil className="w-4 h-4 text-[#5C6B64]" /></button>
                      <button onClick={() => onDelete(s.id)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
